import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../AuthContext";
import { getTokenOrganisationIds } from "../utils/ownership";
import { listAll, listPlacementHeaders, pageItems } from "../services/PlacingAPI";
import { getDealHeaders, getProducts } from "../services/MrcaiAPI";
import { indexRecords, summarisePlacement } from "../components/placing/placingBook";

// How often the placing pages pick up new records while they're on screen
export const REFRESH_INTERVAL_MS = 60 * 1000;

const RESOURCES = {
  submissions: "submissions",
  quoteRequests: "quoterequests",
  quotes: "quotes",
  lineRequests: "linerequests",
  offers: "offers",
  signedLineAdvices: "signedlineadvices",
};

/**
 * Loads every placing record the user can see, plus deal and product names,
 * and summarises the placements their organisations own. Reloads quietly
 * every minute while the page is visible, and when the user comes back to
 * the tab, so new submissions and responses appear on their own.
 * Returns { loading, refreshing, lastUpdated, error, records, summaries,
 * mine, dealsById, productNames, reload }.
 */
export const usePlacingBook = () => {
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, refreshing: false, lastUpdated: null, error: null, records: null, dealsById: {}, productNames: {} });
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    if (inFlight.current) return; // don't stack refreshes on a slow API
    inFlight.current = true;
    setState((s) => (s.records ? { ...s, refreshing: true } : s));
    try {
      const [placements, ...lists] = await Promise.all([
        listPlacementHeaders().then(pageItems),
        ...Object.values(RESOURCES).map((r) => listAll(r).catch(() => [])),
      ]);
      const records = { placements, ...Object.fromEntries(Object.keys(RESOURCES).map((k, i) => [k, lists[i]])) };
      const [deals, products] = await Promise.all([
        getDealHeaders().catch(() => []),
        getProducts().catch(() => []),
      ]);
      setState({
        loading: false,
        refreshing: false,
        lastUpdated: new Date(),
        error: null,
        records,
        dealsById: Object.fromEntries(deals.map((d) => [d._id, d])),
        productNames: Object.fromEntries(products.map((p) => [p.code, p.name || p.code])),
      });
    } catch (err) {
      // Keep showing the last good data if a background refresh fails
      setState((s) => ({ ...s, loading: false, refreshing: false, error: err }));
    } finally {
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    load();
    const isVisible = () => document.visibilityState === "visible";
    const timer = setInterval(() => isVisible() && load(), REFRESH_INTERVAL_MS);
    const onVisibilityChange = () => isVisible() && load();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [load]);

  const myOrgIds = getTokenOrganisationIds(user);
  const myOrgKey = myOrgIds.join(",");
  const mine = useCallback((record) => myOrgKey.split(",").includes(record?.organisationOwnerId), [myOrgKey]);

  const summaries = useMemo(() => {
    if (!state.records) return [];
    const index = indexRecords(state.records);
    return state.records.placements.filter(mine).map((p) => summarisePlacement(p, index));
  }, [state.records, mine]);

  return { ...state, summaries, mine, reload: load };
};
